import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import Link from '@components/Link';

import { timeAgo } from '@util/date';
import { pluralize, truncate } from '@util/strings';

import { getRegistry } from '@constants/registries';

import Tag from '@components/Tag';

import * as styles from './PackageCard.module.css';

const PackageCardScore = ({ name, score }) => {
  return (
    <div className={styles.score} aria-label={`${name} score of ${score}`}>
      <div
        aria-hidden
        className={styles.scoreBar}
        style={{ '--score-progress': `${score}%` }}
      />
      <span aria-hidden className={styles.scoreName}>
        <strong className={styles.scoreValue}>{score}</strong>
        {name}
      </span>
    </div>
  );
};

const PackageCard = ({
  name,
  registry,
  keywords = [],
  description,
  contributors = {},
  scores,
  latestVersion,
  licenses = [],
  vulnerabilities = 0,
  isExactMatch,
  lastReleaseDate,
  href,
}) => {
  const registryInfo = getRegistry(registry);
  const RegistryIcon = registryInfo.icon;
  const state = vulnerabilities > 0 ? 'vulnerabilities' : null;

  const hasScores = useMemo(() => {
    return (
      scores.quality.score != null &&
      scores.maintenance.score != null &&
      scores.docs.score != null
    );
  }, [scores]);

  const inner = (
    <article className={styles.root} data-state={state}>
      <header className={styles.header}>
        <h2 className={styles.title}>
          <span aria-hidden className={styles.registry}>
            <RegistryIcon />
          </span>
          <span className={styles.name}>
            <span>{name}</span>
            {isExactMatch && <Tag variant="highlight">Exact match</Tag>}
          </span>
        </h2>
        {vulnerabilities > 0 && (
          <div className={styles.tags}>
            <Tag variant="alert">
              {vulnerabilities} current{' '}
              {pluralize('vulnerability', 'vulnerabilities', vulnerabilities)}
            </Tag>
          </div>
        )}
      </header>
      <main className={styles.main}>
        {description && (
          <div className={styles.description}>{truncate(description, 50)}</div>
        )}
        {keywords.length > 0 && (
          <ol aria-label="Keywords" className={styles.keywords}>
            {keywords.map((keyword) => (
              <Tag key={keyword} as="li">
                {keyword}
              </Tag>
            ))}
          </ol>
        )}
      </main>
      <footer className={styles.footer}>
        {contributors != '0' && contributors != null && (
          <span>{contributors} Contributors</span>
        )}
        <div className={styles.versionInformation}>
          {latestVersion && (
            <span className={styles.version}>{latestVersion}</span>
          )}
          {lastReleaseDate && (
            <span className={styles.released}>
              published {timeAgo(lastReleaseDate)}
            </span>
          )}
          {licenses.length > 0 && (
            <span className={styles.licenses}>{licenses.join(', ')}</span>
          )}
        </div>
      </footer>
      {hasScores && (
        <aside className={styles.scores}>
          <PackageCardScore name="Quality" score={scores.quality.score} />
          <PackageCardScore
            name="Maintenance"
            score={scores.maintenance.score}
          />
          <PackageCardScore name="Docs" score={scores.docs.score} />
        </aside>
      )}
    </article>
  );

  return href ? (
    <Link href={href} className={styles.link}>
      {inner}
    </Link>
  ) : (
    inner
  );
};

PackageCard.propTypes = {
  props: PropTypes.object,
};

export default PackageCard;
